package mapsearch.globalsearch

import analytics.AnalyticsCategory
import analytics.AnalyticsService
import analytics.AnalyticsTags
import apiclient.FormationClient
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectVisibility
import apiclient.mapsearch.MapMarkerResult
import apiclient.mapsearch.SearchSpec
import apiclient.mapsearch.globalSearch
import apiclient.tags.init
import apiclient.tags.tag
import auth.Features
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import koin.koinCtx
import koin.withKoin
import kotlin.coroutines.EmptyCoroutineContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.conflate
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.launch
import mapsearch.SearchLead
import mapsearch.SearchSwitch
import overlays.BusyStore
import twcomponents.doIfFeatureFlagEnabledSuspend

val SearchSpec.suitableForGeocoding: Boolean
    get() {
        return this.near != null && (this.query?.length ?: 0) > 5
    }

val SearchSpec.queryMatchesForGeocoding: Boolean
    get() {
        return this.query?.matches("""^\s*(\d{1,5}\s+\p{L}.*|\p{L}[\p{L}\s\.\-]+?\s+\d{1,5}[a-zA-Z]?)\s*(?:,\s*\p{L}.*)?${'$'}""".toRegex()) == true
    }

class GlobalSearchListResultsStore : RootStore<List<MapMarkerResult>>(emptyList(), Job()) {
    private val searchSwitch: SearchSwitch = koinCtx.get()
    private val globalSearchSpecStore: GlobalSearchSpecStore = koinCtx.get()
    private val geocodingResultsStore: GeocodingResultsStore = koinCtx.get()
    private val analyticsService by koinCtx.inject<AnalyticsService>()

    private val globalSearch = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            withKoin {
                val busyStore: BusyStore = get()
                val formationClient: FormationClient = get()

                val context = globalSearchSpecStore.current

                if (context.ownerIds.isNotEmpty()) {
                    console.log("-> GLOBAL-SEARCH")
                    CoroutineScope(EmptyCoroutineContext).launch {
                        busyStore.handleApiCall(
                            supplier = suspend {
                                formationClient.globalSearch(
                                    context.copy(
                                        // construct searchCtx
                                        objectTypes = context.objectTypes
                                            ?.filter { it.includeInSearch }
                                            ?.takeUnless { it.isEmpty() }
                                            ?: globalSearchObjectTypes,
                                        excludeTags = context.excludeTags.init() + listOfNotNull(
                                            ObjectTags.Visibility.tag(ObjectVisibility.Hidden),
                                            ObjectTags.Flagged.tag(true),
                                            ObjectTags.IsArchetype.tag(true),
                                        ),
                                    ),
                                    size = 100,
//                                .also {
//                                    console.log("searching objTypes", context.objectTypes?.toTypedArray())
//                                    console.log("searching", it)
//                                },
                                )
                            },
                            processResult = { results ->
                                update(results.results) // store is used for displaying results in the searchResultsList

                                Features.EnableGeocoding.doIfFeatureFlagEnabledSuspend {
                                    if (context.suitableForGeocoding && (results.results.size < 5)
                                        || context.queryMatchesForGeocoding
                                    ) {
                                        geocodingResultsStore.geocode(context)
                                    } else if (context.query.isNullOrBlank()) {
                                        geocodingResultsStore.reset()
                                    }
                                }
                            },
                            processError = { throwable ->
                                console.log("GlobalSearch failed.", throwable)
                            },
                        )
                    }
                }
            }
        }
    }

    fun reset() {
        update(emptyList())
        geocodingResultsStore.reset()
    }

    // emits analytics events once the user has paused typing long enough
    private val sendAnalytics = handle<Pair<String, List<String>>> { current, (query, tags) ->
        val resultSize = current.size
        analyticsService.withAnalytics(AnalyticsCategory.DoSearch) {
            search(target = query, value = resultSize.toDouble(), tags = tags)
        }
        current
    }

    init {
        val rawFlow = combine(searchSwitch.data, globalSearchSpecStore.data) { sw, searchSpec -> Pair(sw, searchSpec) }.mapNotNull { (sw, searchSpec) ->
            if (sw == SearchLead.Global) searchSpec else null
        }

        val (globalSearchContext, analyticsQueries) =
            rawFlow.splitFlowForAnalytics(
                toQuery = { ctx -> ctx.query?.trim() ?: "" },
                toTags = { ctx ->
                    ctx.tags?.map { AnalyticsTags.Kw.tag(it) } ?: emptyList()
                },
            )

        globalSearchContext.map { }.conflate() handledBy globalSearch
        analyticsQueries handledBy sendAnalytics
    }
}
